import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getAllRecurringTransactions } from "src/api/recurring-transactions";
import { useMemo } from "react";
import moment from "moment";
import { APP_USER_ID_KEY } from "src/constants/app";
import { CACHE_KEYS } from "src/constants/cache";

const REFETCH_MINUTES = 10;
const REFETCH_INTERVAL = REFETCH_MINUTES * 1000 * 60;

const UPCOMING_PAYMENT_DAYS = 3;

export default function useFrequentTransactions() {
  const queryClient = useQueryClient();
  const currentUserId = localStorage?.getItem?.(APP_USER_ID_KEY) || "";

  const {
    data: frequentTransactions,
    isLoading,
    isRefetching,
  } = useQuery({
    queryFn: async () => await getAllRecurringTransactions(),
    queryKey: [CACHE_KEYS.ALL_FREQUENT_TRANSACTIONS, currentUserId],
    refetchInterval: REFETCH_INTERVAL,
  });

  const resetCache = async () => {
    return await queryClient.refetchQueries({
      queryKey: [CACHE_KEYS.ALL_FREQUENT_TRANSACTIONS, currentUserId],
    });
  };

  const allFrequentTransactions = useMemo(() => {
    return frequentTransactions?.data || [];
  }, [frequentTransactions?.data]);

  const today = useMemo(() => moment().startOf("day"), []);

  const todayPayments = useMemo(() => {
    return allFrequentTransactions.filter((item: any) => {
      if (!item?.enabled || !item?.nextPayment) return false;

      return moment(item.nextPayment).startOf("day").isSame(today, "day");
    });
  }, [allFrequentTransactions, today]);

  const upcomingPayments = useMemo(() => {
    return allFrequentTransactions.filter((item: any) => {
      if (!item?.enabled || !item?.nextPayment) return false;

      const nextPayment = moment(item.nextPayment).startOf("day");
      const daysUntilPayment = nextPayment.diff(today, "days");

      return daysUntilPayment > 0 && daysUntilPayment <= UPCOMING_PAYMENT_DAYS;
    });
  }, [allFrequentTransactions, today]);

  const recentOverduePayments = useMemo(() => {
    return allFrequentTransactions.filter((item: any) => {
      if (!item?.enabled || !item?.nextPayment) return false;

      const nextPayment = moment(item.nextPayment).startOf("day");

      return nextPayment.isBefore(today, "day");
    });
  }, [allFrequentTransactions, today]);

  const scheduledPayments = useMemo(() => {
    return allFrequentTransactions.filter((item: any) => {
      if (!item?.enabled || !item?.nextPayment) return false;

      const nextPayment = moment(item.nextPayment).startOf("day");
      const daysUntilPayment = nextPayment.diff(today, "days");

      return daysUntilPayment > UPCOMING_PAYMENT_DAYS;
    });
  }, [allFrequentTransactions, today]);

  return {
    allFrequentTransactions,

    todayPayments,
    upcomingPayments,
    recentOverduePayments,
    scheduledPayments,

    isLoadingFrequentTransactions: !!isLoading,
    isRefetchingFrequentTransactions: !!isRefetching,
    resetCache,
  };
}
